import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Link,
  Stack,
  Text,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react';
import { BiError } from 'react-icons/bi';
import { MdOutlineHiking } from 'react-icons/md';
import SuggestSection from './SuggestSection.jsx';
import TripBuilder from '../Trip/TripBuilder.jsx';
import { shallow } from 'zustand/shallow';
import { useUIStore } from '#store';

export default function Suggest() {
  const {
    tawk,
    suggestions,
    showSuggestions,
    noSearchResults,
    matchedTripParents,
    searchInput,
    setShowSuggestions,
  } = useUIStore(
    (state) => ({
      tawk: state.tawk,
      suggestions: state.suggestedResults,
      showSuggestions: state.showSuggestions,
      noSearchResults: state.noSearchResults,
      matchedTripParents: state.matchedTripParents,
      searchInput: state.searchInput,
      setShowSuggestions: state.setShowSuggestions,
    }),
    shallow
  );
  const { isOpen: isTripOpen, onOpen: onTripOpen, onClose: onTripClose } =
    useDisclosure();
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const showTripCta = matchedTripParents?.length > 0;
  const showNoResults = noSearchResults && !showTripCta;
  const singleTripMatch =
    matchedTripParents?.length === 1 ? matchedTripParents[0] : null;

  if (!showSuggestions && !isTripOpen) return null;

  return (
    <>
      {showSuggestions && (
        <Flex w="100%" position="relative">
          <Flex
            w="100%"
            align="stretch"
            direction="column"
            background="white"
            zIndex={4}
            maxH="30vh"
            position="absolute"
            boxShadow="0 1px 3px 0 rgb(60 64 67 / 30%), 0 4px 8px 3px rgb(60 64 67 / 15%)"
            fontWeight={400}
            fontStretch="normal"
            fontSize="14px"
            lineHeight="20px"
            fontFamily="Roboto, Arial, sans-serif"
            letterSpacing="0.2px"
            overflow="hidden"
          >
            {showTripCta && (
              <Box
                bg="brand.50"
                borderBottomWidth="1px"
                borderColor="brand.100"
                flexShrink={0}
              >
                <Flex
                  align="center"
                  direction="row"
                  p={2}
                  pl={1}
                  gap={1}
                  _hover={{ background: 'brand.100', cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    // Prevent input blur from swallowing the click
                    e.preventDefault();
                    setShowSuggestions(false);
                    onTripOpen();
                  }}
                >
                  <Icon
                    as={MdOutlineHiking}
                    color="brand.600"
                    boxSize={7}
                    mx={1.5}
                  />
                  <Stack spacing={0} flex={1}>
                    <Text color="brand.700" fontWeight="semibold">
                      Build multi-night hike
                      {singleTripMatch ? ` · ${singleTripMatch.name}` : ''}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Track a full itinerary night by night
                      {!singleTripMatch && matchedTripParents?.length
                        ? ` · ${matchedTripParents.length} trails match`
                        : ''}
                    </Text>
                  </Stack>
                  <Button
                    size="xs"
                    colorScheme="brand"
                    variant="solid"
                    pointerEvents="none"
                    mr={1}
                  >
                    Open
                  </Button>
                </Flex>
              </Box>
            )}
            <Flex direction="column" overflowY="auto" minH={0} flex={1}>
              {suggestions &&
                suggestions.map((suggestion) => {
                  return (
                    <SuggestSection
                      key={suggestion.entity_id}
                      suggestion={suggestion}
                    />
                  );
                })}
              {showNoResults && (
                <Stack spacing={1} px={4} py={3} color="gray.800">
                  <Flex align={'center'}>
                    <Icon as={BiError} mr={2} boxSize={5} color="red.600"></Icon>
                    <Text fontWeight={'semibold'}>No Results Found</Text>
                  </Flex>
                  <Flex>
                    <Text fontSize={'sm'}>Try something else, or </Text>
                    <Link
                      textDecor={'underline'}
                      fontStyle="italic"
                      _hover={{
                        textDecor: 'underline',
                        filter: 'brightness(0.1)',
                      }}
                      onMouseDown={(e) => {
                        tawk.current.maximize();
                      }}
                      ml={1}
                      to=""
                    >
                      ask us to add this.
                    </Link>
                  </Flex>
                </Stack>
              )}
            </Flex>
          </Flex>
        </Flex>
      )}
      <Drawer
        isOpen={isTripOpen}
        onClose={onTripClose}
        placement={isMobile ? 'bottom' : 'right'}
        size={isMobile ? 'full' : 'lg'}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" color="brand.700">
            Build a Trip
          </DrawerHeader>
          <DrawerBody py={4}>
            <TripBuilder
              onClose={onTripClose}
              initialSearch={searchInput}
              initialTrack={singleTripMatch}
            />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
